import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Info, Clock, AlertTriangle } from 'lucide-react';
import api from '../../../services/api';
import { AuthContext } from '../../../context/AuthContext';
import RequestHistoryModal from '../../../components/RequestHistoryModal';
import RequestDetailsModal from '../../../components/RequestDetailsModal';

const Leaves = () => {
  const { user } = useContext(AuthContext);
  const isIntern = user?.role === 'Intern' || (user?.designation && user.designation.toLowerCase().includes('intern'));
  
  const [balances, setBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [currentHistory, setCurrentHistory] = useState([]);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    leaveType: '',
    startDate: '',
    endDate: '',
    reason: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [balRes, reqRes, typesRes] = await Promise.all([
        api.get('/leaves/balances/me'),
        api.get('/leaves/me'),
        api.get('/leaves/types')
      ]);
      setBalances(balRes.data);
      setRequests(reqRes.data);
      setLeaveTypes(typesRes.data);
      if (typesRes.data.length > 0) {
        // Default to paid leave if present
        const paid = typesRes.data.find(t => t.isPaid) || typesRes.data[0];
        setFormData(prev => ({ ...prev, leaveType: prev.leaveType || paid._id }));
      }
    } catch (err) {
      console.error('Error fetching leaves', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.post('/leaves', formData);
      setShowForm(false);
      fetchData();
      const paid = leaveTypes.find(t => t.isPaid) || leaveTypes[0];
      setFormData({ leaveType: paid?._id || '', startDate: '', endDate: '', reason: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit leave request');
    }
  };

  // Helper to preview working days (excluding Sunday) and paid vs unpaid breakdown
  const calculatePreviewDays = () => {
    if (!formData.startDate || !formData.endDate) return null;
    let count = 0;
    let curr = new Date(formData.startDate);
    curr.setHours(0, 0, 0, 0);
    const end = new Date(formData.endDate);
    end.setHours(0, 0, 0, 0);

    if (curr > end) return { error: 'Start date cannot be after end date' };

    while (curr <= end) {
      if (curr.getDay() !== 0) count++;
      curr.setDate(curr.getDate() + 1);
    }

    if (isIntern) {
      return { total: count, paid: 0, unpaid: count, isPaidType: false, isIntern: true };
    }

    const selectedType = leaveTypes.find(t => t._id === formData.leaveType);
    const paidBal = balances.find(b => b.leaveType?.isPaid);
    const remaining = paidBal ? paidBal.remaining : 0;

    let paid = 0;
    let unpaid = count;

    const isEligiblePaidType = selectedType?.isPaid;

    if (selectedType && isEligiblePaidType) {
      // Max 4 paid leaves at once in a month if leave balance is available
      const maxPaidAllowed = Math.min(count, 4, remaining);
      paid = maxPaidAllowed;
      unpaid = Math.max(0, count - paid);
    }

    return { total: count, paid, unpaid, isPaidType: isEligiblePaidType, isIntern: false };
  };

  const preview = calculatePreviewDays();

  const getStatusColor = (status) => {
    switch(status) {
      case 'APPROVED': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'REJECTED': return 'bg-rose-100 text-rose-800 border-rose-200';
      case 'CANCELLED': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-amber-100 text-amber-800 border-amber-200';
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto w-full min-w-0">
      {/* Header and Balance Display */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">My Leaves</h1>
            {isIntern && (
              <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-amber-200">
                Intern Profile
              </span>
            )}
          </div>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
            {isIntern ? 'Interns are eligible for unpaid leaves only' : `Annual leave quota for year ${currentYear}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 sm:gap-4 w-full md:w-auto">
          {balances.filter(b => b.allocated > 0 || b.leaveType?.isPaid).map(b => (
            <div key={b._id} className="bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-100 border border-purple-200 px-4 py-2.5 rounded-2xl flex items-center justify-between sm:justify-start gap-4 shadow-sm w-full sm:w-auto">
              <div className="flex flex-col">
                <span className="text-purple-900 font-semibold text-sm">{b.leaveType?.name || 'Paid Leave'}</span>
                <span className="text-[11px] text-purple-600 font-medium">Year {b.year || currentYear}</span>
              </div>
              <div className="flex items-center gap-3 bg-white/80 px-3 py-1.5 rounded-xl border border-purple-200/80 shadow-xs">
                <div className="flex items-baseline gap-1 bg-purple-600 text-white font-bold px-3 py-1 rounded-lg shadow-inner">
                  <span className="text-lg sm:text-xl leading-none">{b.remaining}</span>
                  <span className="text-[11px] font-normal opacity-90">Left</span>
                </div>
                <div className="flex flex-col text-[11px] sm:text-xs text-purple-900 font-medium border-l border-purple-200 pl-2.5 leading-tight">
                  <span className="text-gray-600">Total: <strong className="text-gray-900">{b.allocated}</strong></span>
                  <span className="text-gray-600">Used: <strong className="text-purple-700">{b.used}</strong></span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap items-center justify-end gap-3 mb-6">
        <Link
          to="/employee/holidays"
          className="border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 px-4 sm:px-5 py-2.5 rounded-xl transition-all font-semibold text-sm sm:text-base flex items-center justify-center gap-2 w-full sm:w-auto shadow-sm"
        >
          <Calendar className="w-4 h-4 text-purple-600" />
          View Company Holidays
        </Link>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-5 py-2.5 rounded-xl shadow-md transition-all font-medium text-sm sm:text-base flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          {showForm ? 'Cancel Application' : '+ Apply for Leave'}
        </button>
      </div>

      {/* Apply For Leave Modal/Card */}
      {showForm && (
        <div className="bg-white p-5 sm:p-7 rounded-2xl shadow-lg border border-gray-100 mb-8 transition-all">
          <div className="flex items-center justify-between mb-4 border-b pb-3">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Apply for Leave</h2>
              <p className="text-xs text-gray-500">Submit a leave request for manager and HR approval</p>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${
              isIntern 
                ? 'bg-amber-50 text-amber-700 border-amber-200' 
                : 'bg-blue-50 text-blue-700 border-blue-200'
            }`}>
              {isIntern ? 'Intern: Unpaid Leaves Only' : '12 Days / Year Quota'}
            </span>
          </div>

          {/* Policy Information Alert */}
          <div className={`mb-5 border p-3.5 rounded-xl text-xs sm:text-sm flex items-start gap-2.5 ${
            isIntern 
              ? 'bg-amber-50/70 border-amber-200 text-amber-900' 
              : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 text-blue-900'
          }`}>
            <Info className={`w-4 h-4 shrink-0 mt-0.5 ${isIntern ? 'text-amber-600' : 'text-blue-600'}`} />
            {isIntern ? (
              <div className="space-y-1">
                <p className="font-semibold text-amber-950">Intern Leave Policy:</p>
                <p className="text-amber-900">
                  Interns are <strong>not eligible for paid leaves</strong> as per company policy. Any leaves applied and approved will be processed as <strong>Unpaid Leave (Loss of Pay)</strong>.
                </p>
                <p className="text-amber-900 font-medium pt-1 border-t border-amber-200/60 text-[11px] sm:text-xs">
                  🔒 <strong>Approval Required:</strong> Every leave request must still be approved by your Manager / HR.
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                <p className="font-semibold text-blue-950">Company Leave Policy:</p>
                <p className="text-blue-800">
                  You have an annual Paid Leave quota. If you have paid leave balance remaining, your approved leaves will be <strong>Paid Leave</strong>. If you run out of balance (or exceed monthly limits), days are automatically processed as <strong>Unpaid Leave (Loss of Pay)</strong>.
                </p>
                <p className="text-blue-900 font-medium pt-1 border-t border-blue-200/60 text-[11px] sm:text-xs">
                  🔒 <strong>Approval Required:</strong> Every leave request must be approved by your Manager / HR. Your leave balance will only be deducted after official approval.
                </p>
              </div>
            )}
          </div>

          {error && <div className="text-red-600 mb-4 bg-red-50 border border-red-200 p-3 rounded-xl text-sm">{error}</div>}

          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Leave Type</label>
              {leaveTypes.length <= 1 ? (
                <div className="w-full p-2.5 border border-gray-200 bg-gray-50 rounded-xl text-sm font-medium text-gray-700 flex items-center justify-between">
                  <span>{leaveTypes[0]?.name || 'Paid Leave'}</span>
                  <span className="text-xs text-gray-500 font-normal">
                    {isIntern ? '(Unpaid for Interns)' : (balances.find(b => b.leaveType?.isPaid)?.remaining > 0 ? `${balances.find(b => b.leaveType?.isPaid)?.remaining} balance left` : 'No paid balance left (Unpaid)')}
                  </span>
                </div>
              ) : (
                <select 
                  value={formData.leaveType}
                  onChange={e => setFormData({...formData, leaveType: e.target.value})}
                  className="w-full p-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                >
                  {leaveTypes.map(t => (
                    <option key={t._id} value={t._id}>
                      {t.name} {isIntern ? '(Unpaid for Interns)' : t.isPaid ? '(Paid Leave quota)' : '(Unpaid)'}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Reason</label>
              <input 
                type="text" required
                placeholder="Reason for leave..."
                value={formData.reason}
                onChange={e => setFormData({...formData, reason: e.target.value})}
                className="w-full p-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Start Date</label>
              <input 
                type="date" required
                value={formData.startDate}
                onChange={e => setFormData({...formData, startDate: e.target.value})}
                className="w-full p-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">End Date</label>
              <input 
                type="date" required
                value={formData.endDate}
                onChange={e => setFormData({...formData, endDate: e.target.value})}
                className="w-full p-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            {/* Dynamic Days Breakdown Preview */}
            {preview && !preview.error && preview.total > 0 && (
              <div className="col-span-1 sm:col-span-2 bg-gray-50 border border-gray-200 rounded-xl p-3.5 flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-purple-600 shrink-0" />
                  <span className="font-medium text-gray-800">
                    Calculated Working Days: <strong>{preview.total} days</strong> (excluding Sundays)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {preview.isIntern ? (
                    <span className="px-2.5 py-1 bg-amber-100 text-amber-800 font-semibold rounded-lg border border-amber-200">
                      {preview.total} Unpaid Days (Intern Policy)
                    </span>
                  ) : preview.isPaidType ? (
                    <>
                      <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 font-semibold rounded-lg border border-emerald-200">
                        {preview.paid} Paid {preview.paid === 1 ? 'Day' : 'Days'}
                      </span>
                      {preview.unpaid > 0 && (
                        <span className="px-2.5 py-1 bg-amber-100 text-amber-800 font-semibold rounded-lg border border-amber-200 flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          {preview.unpaid} Unpaid (no balance left / exceeds monthly cap)
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="px-2.5 py-1 bg-gray-200 text-gray-800 font-semibold rounded-lg">
                      {preview.total} Unpaid Days
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="col-span-1 sm:col-span-2 text-right pt-2">
              <button 
                type="submit" 
                className="bg-blue-600 text-white px-6 py-2.5 rounded-xl hover:bg-blue-700 w-full sm:w-auto font-medium text-sm shadow-md transition-all"
              >
                Submit Application
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Leave History Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200">
        <RequestHistoryModal 
          isOpen={historyModalOpen} 
          onClose={() => setHistoryModalOpen(false)} 
          history={currentHistory} 
        />
        <RequestDetailsModal
          isOpen={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          request={selectedRequest}
          type="LEAVE"
        />
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-800 text-sm sm:text-base">Leave History</h3>
            <p className="text-xs text-gray-500">Track the status of your submitted leave applications</p>
          </div>
          <span className="text-xs text-gray-400 block sm:hidden">Swipe sideways &rarr;</span>
        </div>
        <div className="overflow-x-auto w-full custom-scrollbar">
          <table className="w-full min-w-[650px] divide-y divide-gray-200">
            <thead className="bg-gray-50/80">
              <tr>
                <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Leave Type</th>
                <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Dates</th>
                <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Days Breakdown</th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Reason</th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Track</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">Loading leave requests...</td></tr>
              ) : requests.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No leave requests found</td></tr>
              ) : (
                requests.map(r => (
                  <tr key={r._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800">
                      {r.leaveType?.name || 'Leave'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(r.startDate).toLocaleDateString()} - {new Date(r.endDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-800">{r.days} {r.days === 1 ? 'Day' : 'Days'}</span>
                        {(() => {
                          const isBothApproved = (r.managerStatus === 'APPROVED' && (r.adminStatus === 'APPROVED' || r.hrStatus === 'APPROVED'));
                          const isAdminApproved = r.adminStatus === 'APPROVED';
                          const isOverallApproved = r.status === 'APPROVED';

                          // If approved by admin, manager, or final status is APPROVED for a paid leave type
                          if (isBothApproved || isAdminApproved || (isOverallApproved && (r.leaveType?.isPaid || r.leaveType?.name === 'Paid Leave' || r.leaveType?.name === 'Casual Leave'))) {
                            const paidCount = (r.paidDays !== undefined && Number(r.paidDays) > 0) ? r.paidDays : r.days;
                            const unpaidCount = (r.unpaidDays !== undefined && r.paidDays !== undefined && Number(r.paidDays) > 0) ? r.unpaidDays : Math.max(0, r.days - paidCount);
                            return (
                              <div className="flex items-center gap-1.5 text-[11px] mt-0.5">
                                <span className="text-emerald-700 font-semibold">{paidCount} Paid (Full Day)</span>
                                {unpaidCount > 0 && (
                                  <span className="text-amber-700 font-semibold">| {unpaidCount} Unpaid</span>
                                )}
                              </div>
                            );
                          }

                          const isPaid = (r.paidDays > 0) || r.leaveType?.isPaid || r.leaveType?.name === 'Paid Leave' || r.leaveType?.name === 'Casual Leave' || (r.status === 'APPROVED');
                          if (isPaid) {
                            const paidCount = (r.paidDays !== undefined && Number(r.paidDays) > 0) 
                              ? r.paidDays 
                              : (r.status === 'APPROVED' ? r.days : (r.paidDays !== undefined && Number(r.paidDays) > 0 ? r.paidDays : r.days));
                            const unpaidCount = (r.unpaidDays !== undefined && r.paidDays !== undefined && Number(r.paidDays) > 0) ? r.unpaidDays : Math.max(0, r.days - paidCount);
                            return (
                              <div className="flex items-center gap-1.5 text-[11px] mt-0.5">
                                <span className="text-emerald-700 font-semibold">{paidCount} Paid</span>
                                {unpaidCount > 0 && (
                                  <span className="text-amber-700 font-semibold">| {unpaidCount} Unpaid</span>
                                )}
                              </div>
                            );
                          }

                          return <span className="text-[11px] text-gray-500">Unpaid</span>;
                        })()}
                      </div>
                    </td>
                    <td 
                      className="px-6 py-4 text-sm text-gray-700 max-w-sm whitespace-normal break-words cursor-pointer hover:text-blue-600 transition-colors"
                      title="Click to view full reason"
                      onClick={() => {
                        setSelectedRequest(r);
                        setDetailsModalOpen(true);
                      }}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="line-clamp-2">{r.reason}</span>
                        <span className="text-[10px] bg-blue-50 text-blue-600 font-semibold px-1.5 py-0.5 rounded shrink-0">View</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-1 inline-flex text-xs leading-4 font-semibold rounded-full border ${getStatusColor(r.status)}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => {
                          setCurrentHistory(r.history || []);
                          setHistoryModalOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 font-medium text-xs underline"
                      >
                        Track
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Leaves;
